import { Badge, Group, Loader, Text, Transition } from "@mantine/core";
import { IconWifiOff, IconCloudUpload } from "@tabler/icons-react";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { useOfflineSync } from "@/hooks/useOfflineSync";

/**
 * Offline indicator component that displays when the app is offline
 * Also shows sync status when there are pending items
 */
export function OfflineIndicator() {
  const { isOffline } = useOnlineStatus();
  const { isSyncing, pendingCount, hasPendingItems } = useOfflineSync();

  // Show banner if offline OR if there are pending items to sync
  const showBanner = isOffline || hasPendingItems;

  // Determine banner content based on state
  const getBannerContent = () => {
    if (isOffline) {
      return {
        color: "var(--mantine-color-orange-6)",
        icon: <IconWifiOff size={16} color="white" />,
        text: `You're offline${pendingCount > 0 ? ` — ${pendingCount} item${pendingCount > 1 ? "s" : ""} waiting to sync` : " — Changes will sync when connection is restored"}`,
      };
    }

    if (isSyncing) {
      return {
        color: "var(--mantine-color-blue-6)",
        icon: <Loader size={14} color="white" />,
        text: `Syncing ${pendingCount} item${pendingCount > 1 ? "s" : ""}...`,
      };
    }

    if (hasPendingItems) {
      return {
        color: "var(--mantine-color-yellow-6)",
        icon: <IconCloudUpload size={16} color="white" />,
        text: `${pendingCount} item${pendingCount > 1 ? "s" : ""} pending sync`,
      };
    }

    return null;
  };

  const content = getBannerContent();

  return (
    <Transition mounted={showBanner && !!content} transition="slide-down" duration={300}>
      {(styles) => (
        <div
          style={{
            ...styles,
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            backgroundColor: content?.color || "var(--mantine-color-orange-6)",
            padding: "8px 16px",
          }}
        >
          <Group justify="center" gap="xs">
            {content?.icon}
            <Text size="sm" c="white" fw={500}>
              {content?.text}
            </Text>
          </Group>
        </div>
      )}
    </Transition>
  );
}

/**
 * Compact offline badge for use in specific components
 * Useful for forms and input areas to indicate offline status
 */
export function OfflineBadge() {
  const { isOffline } = useOnlineStatus();

  if (!isOffline) return null;

  return (
    <Badge
      variant="light"
      color="orange"
      size="sm"
      leftSection={<IconWifiOff size={12} />}
    >
      Offline
    </Badge>
  );
}

/**
 * Sync status badge for individual items (like daily logs)
 * Shows pending/syncing/error status
 */
export function SyncStatusBadge({
  status,
  error,
}: {
  status?: "pending" | "syncing" | "synced" | "error";
  error?: string;
}) {
  if (!status || status === "synced") return null;

  const getContent = () => {
    switch (status) {
      case "pending":
        return {
          color: "yellow",
          icon: <IconCloudUpload size={12} />,
          text: "Pending sync",
        };
      case "syncing":
        return {
          color: "blue",
          icon: <Loader size={10} />,
          text: "Syncing...",
        };
      case "error":
        return {
          color: "red",
          icon: <IconWifiOff size={12} />,
          text: error || "Sync failed",
        };
      default:
        return null;
    }
  };

  const content = getContent();
  if (!content) return null;

  return (
    <Badge
      variant="light"
      color={content.color}
      size="sm"
      leftSection={content.icon}
      title={error}
    >
      {content.text}
    </Badge>
  );
}

export default OfflineIndicator;
